import type { CameraAttributeValues } from '../map/attribute-camera';
import { createPortal } from 'react-dom';
import { useEffect, useMemo, useRef, useState, type RefObject } from 'react';
import { compositionUrl } from '../../../renderer/src/composition-url';
import { resolveOutput } from '../../../renderer/src/presets';
import type { VideoRequest } from '../services/local-video';
import { useCompositionPreview } from '../hooks/use-composition-preview';
import type { CompositionElement } from '../render/preview-diagnostics';
import { PlatformChrome } from './PlatformChrome';
import type { ChromeDensity } from '../render/platform-geometry';
import type { PresentationTarget } from '../../../renderer/src/video-presentation';
import type { MapStill } from './LayoutChooser';
import type { DepthOfFieldPreview } from '../../../renderer/src/depth-of-field';
import { usePreviewStageSize } from './use-preview-stage-size';
import { previewFrameScale } from './preview-frame-size';
import { liveComposition } from '../render/live-composition';

export type PreviewMode = 'clean' | 'platform' | 'guides';
interface Properties {
  frameScheduled?: boolean;
  onCameraAttributes?: ((values: CameraAttributeValues | undefined) => void) | undefined;
  errorTarget?: RefObject<HTMLDivElement | null>;
  request: VideoRequest; progress: number; comparison: PresentationTarget; mode: PreviewMode; density: ChromeDensity;
  onMeasure?: ((elements: CompositionElement[]) => void) | undefined; onReady: (ready: boolean) => void;
  onStill?: ((still: MapStill | undefined) => void) | undefined;
  focusPreview?: DepthOfFieldPreview | undefined;
  fitStage?: boolean;
  onCameraAngle?: ((angle: number | undefined) => void) | undefined;
}
interface Snapshot { key: string; url: string; request: VideoRequest; progress: number; width: number; height: number; focusPreview?: DepthOfFieldPreview | undefined }

/** Settings reuse one map; format changes replace it without overlapping WebGL contexts. */
export function CompositionPreview(properties: Properties) {
  const { request, progress, comparison, mode, density, onReady, onMeasure, onStill, focusPreview, fitStage = false } = properties;
  const wrapper = useRef<HTMLDivElement>(null);
  const [reload, setReload] = useState(0);
  const [frameState, setFrameState] = useState({ ready: false, error: '', presentedKey: '' });
  const available = usePreviewStageSize(wrapper, `${mode}:${comparison}:${frameState.error}`);
  // Only output dimensions replace the iframe; settings update its existing map.
  const initial = useMemo(() => request, [request.preset]);
  const output = { ...resolveOutput(initial.preset, initial.durationSeconds, initial.camera, initial.distance, initial.line, initial.cameraStyleVersion ?? 4),
    cameraGimbal: initial.cameraGimbal, placeTitle: initial.placeTitle, exportRange: initial.exportRange, cameraAngle: initial.cameraAngle, depthOfField: initial.depthOfField,
    trackAppearance: initial.trackAppearance, unitSystem: initial.unitSystem, hidePlaces: initial.hidePlaces,
    terrainExaggeration: initial.terrainExaggeration, presentation: initial.presentation };
  const previewUrl = new URL(compositionUrl(window.location.origin, output, initial.mapStyle));
  previewUrl.searchParams.set('preview', String(reload));
  if (onStill) previewUrl.searchParams.set('preview-still', '1');
  const url = previewUrl.href;
  const key = `${request.preset}:${url}:${request.ride.id}:${request.ride.title}`;
  const snapshot: Snapshot = { key, url, request, progress, width: output.width, height: output.height, focusPreview };
  const current = useRef(snapshot);
  current.current = snapshot;
  const scale = previewFrameScale(output, available, fitStage);
  const failure = frameState.error ? <p role="alert">{frameState.error} <button type="button" onClick={() => {
    onReady(false); setReload(value => value + 1);
  }}>Reload preview</button></p> : null;
  return <div className="composition-preview" ref={wrapper} aria-busy={!frameState.ready}>
    <div className="composition-preview-canvas" style={{ width: output.width * scale, height: output.height * scale }}>
      <PreviewFrame key={key} snapshot={snapshot} scale={scale} onMeasure={onMeasure} onStill={onStill}
        frameScheduled={properties.frameScheduled} current={current} onCameraAttributes={properties.onCameraAttributes} onCameraAngle={properties.onCameraAngle} onStatus={(ready, error) => {
          setFrameState(value => {
            const presentedKey = ready ? key : value.presentedKey;
            return value.ready === ready && value.error === error && value.presentedKey === presentedKey
              ? value : { ready, error, presentedKey };
          });
          onReady(ready);
        }} />
      {mode !== 'clean' && <div className="composition-preview-pixels" style={{ width: output.width, height: output.height, transform: `scale(${scale})` }}>
        <PlatformChrome target={comparison} width={output.width} height={output.height} density={density} guides={mode === 'guides'} social={request.presentation?.version === 2} />
      </div>}
    </div>
    {mode === 'platform' && comparison === 'instagram-stories' && request.presentation?.version === 2 && <div className="composer-story-reply" style={{ maxWidth: output.width * scale }} aria-hidden="true">
      <span>Send message…</span><span>♡</span><span>↗</span>
    </div>}
    {properties.errorTarget?.current ? createPortal(failure, properties.errorTarget.current) : failure}
    <p className="composer-hint composition-preview-meta">
      <span>{output.width} × {output.height}{mode !== 'clean' && ' · Chrome and guides appear only in this preview.'}</span>
      <span className="composition-preview-status" role="status">{!frameState.ready && !frameState.error && frameState.presentedKey !== key && 'Preparing trace preview…'}</span>
    </p>
  </div>;
}

function PreviewFrame({ snapshot, scale, frameScheduled, current, onStatus, onMeasure, onStill, onCameraAngle, onCameraAttributes }: {
  frameScheduled?: boolean | undefined;
  onCameraAttributes?: ((values: CameraAttributeValues | undefined) => void) | undefined;
  snapshot: Snapshot; scale: number; current?: RefObject<Snapshot>;
  onStatus?: (ready: boolean, error: string) => void;
  onCameraAngle?: ((angle: number | undefined) => void) | undefined;
  onMeasure?: ((elements: CompositionElement[]) => void) | undefined;
  onStill?: ((still: MapStill | undefined) => void) | undefined;
}) {
  const frame = useRef<HTMLIFrameElement>(null);
  const { request, progress, url } = snapshot;
  const preview = useCompositionPreview(frame, url, request.ride, progress, request.presentation, onMeasure, onStill,
    request.depthOfField, snapshot.focusPreview, request.cinematicSequence, liveComposition(request), onCameraAngle, frameScheduled, onCameraAttributes);
  const callbacks = useRef(onStatus);
  callbacks.current = onStatus;
  const revision = JSON.stringify([url, request.ride.id, request.ride.title, progress, request.presentation, request.depthOfField, snapshot.focusPreview, request.cinematicSequence, liveComposition(request)]);
  // Notify outside React's commit so frame acknowledgments cannot chain parent updates.
  useEffect(() => {
    let active = true;
    queueMicrotask(() => {
      if (!active || !current || current.current.key !== snapshot.key) return;
      callbacks.current?.(preview.status === 'ready', preview.status === 'error' ? preview.error : '');
    });
    return () => { active = false; };
  }, [preview.status, preview.error, revision, current, snapshot.key]);
  return <div className="composition-preview-layer">
    <div className="composition-preview-pixels" style={{ width: snapshot.width, height: snapshot.height, transform: `scale(${scale})` }}>
      <iframe ref={frame} src={url} title="Selected trace video composition" data-preview-playing={frameScheduled ? 'true' : undefined}
        width={snapshot.width} height={snapshot.height} tabIndex={-1} onLoad={() => void preview.onLoad()} />
    </div>
  </div>;
}
